import React, { useState, useRef, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useClickOutside } from "../../../hooks/useClickOutside";
import { useKeydownListener } from "../../../hooks/useKeydownListener";
import ORMfunctions from "../../../hooks/app/general";

interface Station {
  id: string;
  name: string;
  street: string;
  zipcode: string;
  city: string;
  latitude?: number;
  longitude?: number;
  distance?: number;
}

interface AddressSearchProps {
  stationSelected: string;
  stationSelectedObject: Station | null;
  stations: Station[];
  rangelimit: number;
  resultlimit: number;
  regionCodes?: string[];
  useapi: boolean;
  type: "start" | "end";
  labelId?: string;
  onSelect: (stationId: string) => void;
  onCloseEvent: () => void;
}

export const AddressSearch: React.FC<AddressSearchProps> = ({
  stationSelected,
  stationSelectedObject,
  stations,
  rangelimit,
  resultlimit,
  regionCodes = ["DE"],
  useapi,
  type,
  labelId,
  onSelect,
  onCloseEvent,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);

  const [inputValue, setInputValue] = useState("");
  const [stationsResult, setStationsResult] = useState<Station[]>([
    ...stations,
  ]);
  const [stationCurrent, setStationCurrent] = useState<string>(stationSelected);
  const [firstInit, setFirstInit] = useState(true);

  // Google Maps API state
  const [hasAPI, setHasAPI] = useState(false);
  const [AutocompleteSuggestion, setAutocompleteSuggestion] =
    useState<any>(null);
  const [sessionToken, setSessionToken] = useState<any>(null);

  const clientData = useSelector((state: any) => state.general?.clientData);
  const googleStatus = useSelector((state: any) => state.general?.googleStatus);

  const newestRequestIdRef = useRef(0);
  const rawSuggestionsRef = useRef<any[]>([]);

  // Distance calculation functions
  const deg2rad = (deg: number): number => {
    return deg * (Math.PI / 180);
  };

  const getDistanceFromLatLonInKm = (
    lat: number,
    lng: number,
    posUser: { lat: number; lng: number },
  ): number => {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(posUser.lat - lat);
    const dLng = deg2rad(posUser.lng - lng);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat)) *
        Math.cos(deg2rad(posUser.lat)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c;
    return parseInt(d.toString());
  };

  const compare = (a: Station, b: Station) => {
    if (!a.distance && !b.distance) return 0;
    if (!a.distance) return 1;
    if (!b.distance) return -1;
    if (a.distance < b.distance) return -1;
    if (a.distance > b.distance) return 1;
    return 0;
  };

  const setDistance = useCallback(
    async (lat: number, lng: number, onLoad = false) => {
      const resFor = stations.map((element) => {
        if (element.latitude && element.longitude) {
          const distance = getDistanceFromLatLonInKm(
            element.latitude,
            element.longitude,
            { lat, lng },
          );
          return { ...element, distance };
        }
        return { ...element };
      });

      resFor.sort(compare);

      const filtered = resFor
        .filter((s) => s.distance != null && s.distance < rangelimit)
        .slice(0, resultlimit);

      if (filtered.length === 0 || onLoad) {
        setStationsResult(resFor);
      } else {
        setStationsResult(filtered);
      }
    },
    [stations, rangelimit, resultlimit],
  );

  // Initialize Google Maps API
  useEffect(() => {
    setFirstInit(true);
    inputRef.current?.focus();
    if (stationCurrent) {
      const currentElement = document.getElementById(
        `select-${stationCurrent}`,
      ) as HTMLElement;
      if (currentElement) {
        currentElement.scrollIntoView({
          behavior: "instant" as ScrollBehavior,
          block: "nearest",
        });
      }
    }

    if (useapi && googleStatus) {
      setHasAPI(true);

      // Initialize Google Maps Places API
      const initGoogleMaps = async () => {
        try {
          if (window.google && window.google.maps) {
            const places = await window.google.maps.importLibrary("places");
            setAutocompleteSuggestion((places as any).AutocompleteSuggestion);
            setSessionToken(new (places as any).AutocompleteSessionToken());
          }
        } catch (error) {
          console.error("Error initializing Google Maps:", error);
        }
      };

      initGoogleMaps();
    }

    // Get user's geolocation
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        setDistance(position.coords.latitude, position.coords.longitude, true);
      });
    }
  }, [useapi, googleStatus, setDistance]);

  // Local search function
  const searchLocally = (query: string): { found: boolean } => {
    const q = query.trim().toLowerCase();
    if (!q) return { found: false };

    const matched = stations.filter((s) => {
      const city = (s.city ?? "").toLowerCase();
      const zip = (s.zipcode ?? "").toLowerCase();
      return city.startsWith(q) || zip.startsWith(q);
    });

    if (matched.length === 0) return { found: false };
    setStationsResult(matched);
    return { found: true };
  };

  // Handle input changes
  const handleInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    const query = value.trim();

    if (!query) {
      setStationsResult([...stations]);
      return;
    }

    const { found } = searchLocally(query);

    if (found) {
      return;
    }

    if (!AutocompleteSuggestion || !hasAPI) return;

    const requestId = ++newestRequestIdRef.current;
    const request = {
      input: query,
      sessionToken: sessionToken,
      includedRegionCodes: regionCodes,
      includedPrimaryTypes: ["locality", "postal_code"],
    };

    try {
      const { suggestions: res } =
        await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      if (requestId !== newestRequestIdRef.current) return;

      rawSuggestionsRef.current = res;

      if (res.length > 0) {
        const place = res[0].placePrediction.toPlace();
        await place.fetchFields({
          fields: ["location", "formattedAddress", "id"],
        });

        setDistance(place.location.lat(), place.location.lng());
      }
    } catch (err) {
      console.error("Google API Error:", err);
    }
  };

  // Handle station selection
  const handleStationSelect = (stationId: string) => {
    onSelect(stationId);
  };

  // Clear search
  const handleClear = () => {
    setInputValue("");
    setStationsResult([...stations]);
  };

  // Close event
  const handleClose = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement;
      if (target.classList.contains("orm-clear-btn")) return;
      if (firstInit) {
        setFirstInit(false);
        return;
      }
      onCloseEvent();
    },
    [firstInit, onCloseEvent],
  );

  // Keyboard navigation
  const handleKeydown = useCallback(
    (event: KeyboardEvent) => {
      let currentElement = document.getElementById(
        `select-${stationCurrent}`,
      ) as HTMLElement;

      switch (event.key) {
        case "ArrowUp":
        case "ArrowDown":
          event.preventDefault();

          if (!currentElement) {
            const first = containerRef.current?.querySelector(
              ".orm-stationselect__option--search",
            ) as HTMLElement;
            console.log("currentElement", first);
            currentElement = first;
          } else {
            currentElement = (
              event.key === "ArrowUp"
                ? currentElement.previousElementSibling
                : currentElement.nextElementSibling
            ) as HTMLElement;
          }

          if (currentElement && currentElement.hasAttribute("aria-selected")) {
            const value = currentElement.getAttribute("data-value");
            if (value) {
              setStationCurrent(value);
              currentElement.focus();
              currentElement.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
              });
            }
          }
          break;
        case "Enter":
          if (stationCurrent) {
            handleStationSelect(stationCurrent);
          }
          break;
        case "Escape":
          inputRef.current?.focus();
          handleClear();
          break;
        case "Tab":
          setFirstInit(false);
          onCloseEvent();
          break;
        default:
          inputRef.current?.focus();
          break;
      }
    },
    [stationCurrent, onCloseEvent],
  );

  useClickOutside(containerRef, handleClose);
  useKeydownListener(containerRef, handleKeydown);

  return (
    <div className="orm-address-search" ref={containerRef}>
      <div className="orm-input-wrapper">
        <input
          ref={inputRef}
          value={inputValue}
          type="text"
          placeholder="Ort oder Postleitzahl eingeben"
          className="orm-address-input"
          autoComplete="off"
          onChange={handleInput}
          aria-label="Adresssuche"
          aria-autocomplete="list"
          aria-controls={`stationselect-listbox`}
        />
        {inputValue && (
          <button
            className="orm-clear-btn"
            onClick={handleClear}
            aria-label="Eingabe löschen"
            type="button"
          >
            ✕
          </button>
        )}
      </div>
      <div
        className="orm-stationselect__options orm-stationselect__options--search"
        role="listbox"
        tabIndex={0}
        ref={listboxRef}
        aria-labelledby={labelId}
        id="stationselect-listbox"
      >
        {type === "end" && (
          <div
            className="orm-stationselect__option orm-stationselect__option--search"
            onClick={() => handleStationSelect("startend")}
            role="option"
            tabIndex={-1}
            aria-selected={stationSelected === "startend"}
            id="select-startend"
          >
            {ORMfunctions.i18n("general/likeStartstation") ||
              "Wie Startstation"}
          </div>
        )}
        {stationsResult.map((station) => (
          <div
            key={station.id}
            className={
              stationSelected === station.id
                ? "orm-stationselect__option orm-stationselect__option--search selected"
                : "orm-stationselect__option orm-stationselect__option--search"
            }
            onClick={() => handleStationSelect(station.id)}
            role="option"
            tabIndex={-1}
            aria-selected={stationSelected === station.id}
            id={`select-${station.id}`}
            data-value={station.id}
            title={`${station.name} - ${station.street}, ${station.zipcode} ${station.city}`}
          >
            <div className="orm-station__name">
              <strong>{station.name}</strong>
            </div>
            <small className="noBreak overflowHidden textTrim">
              {station.street}
            </small>
            <small className="noBreak overflowHidden textTrim">
              {station.zipcode} {station.city}
            </small>
            {station.distance && (
              <div className="orm-station__distance">
                <i className="fas fa-map-marker-alt"></i> {station.distance} km
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
